'use client';

import { Button } from '#/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';

import EthereumHexString from '#/components/EthereumHexString';
import NetworkAccountAvatar from '#/components/NetworkAccountAvatar';
import { ResponseCode } from '#/constants';
import { useTranslation } from '#/contexts/useTranslation';
import useWatcherAddressRemoveMutation from '#/data/useWatcherAddressRemoveMutation';
import {
  WatcherAddressInfo,
  useMutateWatcherAddressList,
} from '#/data/v1/useWatcherAddressList';
import { Loader2 } from 'lucide-react';
import { ReactNode, useCallback } from 'react';
import { toast } from 'sonner';

interface Props {
  originalRow: WatcherAddressInfo;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  /**
   * DialogTrigger
   */
  children?: ReactNode;
}
export function TableRowActionsRemoveDialog({
  originalRow,
  open,
  onOpenChange,
  children,
}: Props) {
  const translation = useTranslation();

  const { trigger: triggerRemove, isMutating } =
    useWatcherAddressRemoveMutation();
  const onMutateWatcherAddressList = useMutateWatcherAddressList();

  const onRemoveClick = useCallback(async () => {
    const res = await triggerRemove({
      symbol: originalRow.symbol,
      address: originalRow.address,
    });
    if (res?.data.code === ResponseCode.SUCCESS) {
      onOpenChange?.(false);
      onMutateWatcherAddressList();
      toast.success('Address remove successfully.');
    } else {
      toast.error(res?.data.message);
    }
  }, [triggerRemove, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {
              translation.PortfolioDashboard.ManageYourAddresses.RemoveAddress
                .Title
            }
          </DialogTitle>
          <DialogDescription>
            {
              translation.PortfolioDashboard.ManageYourAddresses.RemoveAddress
                .Description
            }
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center text-gray-900 text-base p-2 bg-blue-50 rounded">
          <NetworkAccountAvatar
            symbol={originalRow.symbol}
            address={originalRow.address}
            diameter={24}
            className="w-6 h-6 mr-1.5"
          />
          <EthereumHexString value={originalRow.address} diameter={24} />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">
              {translation.Application.Dialog.CancelButtonLabel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={isMutating}
            onClick={onRemoveClick}
          >
            {isMutating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {
              translation.PortfolioDashboard.ManageYourAddresses.RemoveAddress
                .ButtonLabel
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
